import { useCallback, useMemo, type RefObject } from 'react';
import { EMessageStatus, type IChatMessage } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';

import type { V1QuestionAnswer } from '@/generated/hooks/rain/models';
import {
  epochMsToDate,
  formatDurationUltraCompact,
} from '@/shared/utils/datetime-utils';

import {
  RainCompactionCard,
  RainFollowUpCard,
  RainInterruptCard,
  RainMaxIterationsCard,
  RainSleepCard,
  RainSubAgentTaskCard,
  RainToolActivity,
  RainToolCallCard,
  RainTransferMarker,
  type TRainTransferStatus,
} from '../components';

import {
  COMPACTION_ID_PREFIX,
  FOLLOW_UP_ID_PREFIX,
  INTERRUPT_ID_PREFIX,
  MAX_ITERATIONS_ID_PREFIX,
  SLEEP_ID_PREFIX,
  SUB_AGENT_TASK_ID_PREFIX,
  TOOL_CALL_ID_PREFIX,
  TRANSFER_MARKER_ID_PREFIX,
} from '../constants';
import { resolveRainAgentIdentity } from '../utils/agent-identity';
import { buildCustomMessage } from '../utils/message-helpers';
import type {
  IRainCompactionState,
  IRainToolActivity,
  IRainFollowUpState,
  IRainInterruptState,
  IRainMaxIterationsState,
  IRainSleepState,
  IRainSubAgentTaskState,
  IRainToolCallState,
  IRainTransferState,
  IRainToolRenderContext,
  IRainToolResolvers,
  TRainToolRenderer,
} from './types';
import type { TRainToolVerbosity } from './scope';

interface IUseRainMessageBuildersParams {
  /**
   * How much of the thread's machinery this surface shows
   * (`IRainAgentConfig.toolVerbosity`). Under `full`, copy-only activity rows
   * are skipped and the generic card carries tool identity and timing.
   *
   * Optional, defaulting to `curated`, so the curated presentation is what a
   * caller gets by omission — the same default the config field has.
   */
  toolVerbosity?: TRainToolVerbosity;
  /** The active agent's tool-name -> renderer map (`IRainAgentConfig.toolRenderers`). */
  toolRenderers: Readonly<Record<string, TRainToolRenderer>>;
  /** The active agent's tool-name -> activity-row map (`IRainAgentConfig.toolActivities`). */
  toolActivities: Readonly<Record<string, IRainToolActivity>>;
  /**
   * The active agent's by-shape renderer lookup
   * (`IRainAgentConfig.resolveToolRenderer`), asked only when the exact-name map
   * has no entry.
   */
  resolveToolRenderer?: (name: string) => TRainToolRenderer | undefined;
  /** Resolvers a renderer needs to answer the tool call it renders. */
  resolvers: IRainToolResolvers;
  submitInterruptReplyRef: RefObject<
    (interruptId: string, answers: V1QuestionAnswer[]) => Promise<void>
  >;
  submitToolApprovalRef: RefObject<
    (toolCallId: string, approved: boolean, reason?: string) => Promise<void>
  >;
  sendFollowUpRef: RefObject<(text: string) => Promise<void>>;
  resolveMaxIterationsRef: RefObject<
    (id: string, shouldContinue: boolean) => Promise<void>
  >;
  /** Wakes the thread from a pending sleep (the sleep card's "Wake up" button). */
  wakeThreadRef: RefObject<() => Promise<void>>;
}

export interface IRainMessageBuilders {
  buildInterruptMessage: (state: IRainInterruptState) => IChatMessage;
  buildToolCallMessage: (state: IRainToolCallState) => IChatMessage;
  buildCompactionMessage: (
    id: string,
    state: IRainCompactionState,
  ) => IChatMessage;
  buildSleepMessage: (id: string, state: IRainSleepState) => IChatMessage;
  buildSubAgentTaskMessage: (state: IRainSubAgentTaskState) => IChatMessage;
  buildFollowUpMessage: (state: IRainFollowUpState) => IChatMessage;
  buildMaxIterationsMessage: (state: IRainMaxIterationsState) => IChatMessage;
  /**
   * Builds the inline separator message marking a transfer boundary.
   *
   * @param state - Current transfer state
   * @param rootAgentId - The thread's root agent id (used to detect back-transfers)
   */
  buildTransferMarkerMessage: (
    state: IRainTransferState,
    rootAgentId: string,
  ) => IChatMessage;
}

export function useRainMessageBuilders({
  toolVerbosity,
  toolRenderers,
  toolActivities,
  resolveToolRenderer,
  resolvers,
  submitInterruptReplyRef,
  submitToolApprovalRef,
  sendFollowUpRef,
  resolveMaxIterationsRef,
  wakeThreadRef,
}: IUseRainMessageBuildersParams): IRainMessageBuilders {
  const { t } = useTranslation('rain');

  const isFullVerbosity = toolVerbosity === 'full';

  const buildInterruptMessage = useCallback(
    (state: IRainInterruptState): IChatMessage =>
      buildCustomMessage(
        `${INTERRUPT_ID_PREFIX}${state.interruptId}`,
        <RainInterruptCard
          interruptId={state.interruptId}
          questions={state.questions}
          answered={state.answered}
          answers={state.answers}
          isSubmitting={state.isSubmitting}
          onSubmit={answers =>
            submitInterruptReplyRef.current(state.interruptId, answers)
          }
        />,
      ),
    [submitInterruptReplyRef],
  );

  const buildGenericToolCallMessage = useCallback(
    (state: IRainToolCallState): IChatMessage =>
      buildCustomMessage(
        `${TOOL_CALL_ID_PREFIX}${state.id}`,
        <RainToolCallCard
          name={state.name}
          statusTemplate={state.statusTemplate}
          status={state.status}
          args={state.args}
          result={state.result}
          errorMessage={state.errorMessage}
          awaitingApproval={state.awaitingApproval}
          approvalDecision={state.approvalDecision}
          denialReason={state.denialReason}
          isSubmittingApproval={state.isSubmitting}
          onApprove={() => submitToolApprovalRef.current(state.id, true)}
          onDeny={() => submitToolApprovalRef.current(state.id, false)}
          durationMicros={state.durationMicros}
          showToolIdentity={isFullVerbosity}
        />,
      ),
    [isFullVerbosity, submitToolApprovalRef],
  );

  /*
   * An agent profile maps its own tool names to renderers (main's per-agent
   * runtime split). Consulted BEFORE the built-in chain below so a profile can
   * specialise a tool without touching this hook; anything unmapped falls
   * through to the shared cards.
   */
  const renderContext = useMemo<IRainToolRenderContext>(
    () => ({
      renderGenericCard: buildGenericToolCallMessage,
      renderCustomCard: (state, content) =>
        buildCustomMessage(`${TOOL_CALL_ID_PREFIX}${state.id}`, content),
      resolvers,
    }),
    [buildGenericToolCallMessage, resolvers],
  );

  /*
   * Stays a DEFAULT message deliberately. Grouping these into a work block is
   * `foldWorkRuns`' job, and it folds over the RENDERED list where an entry that
   * draws nothing cannot split a run. Typing the row THINKING instead would
   * hand grouping to lattice, which folds over the raw message list and breaks
   * a run on anything that is not THINKING — including rain's own invisible
   * `generation_complete` marker, so every tool-loop cycle would cut the run.
   *
   * The timestamp is the call's own start, so a settled block can say how long
   * it worked. `startedAtMs` comes off the `tool_call_start` event rather than
   * the clock, or replaying history stamps every row with the same instant.
   */
  const buildToolActivityMessage = useCallback(
    (activity: IRainToolActivity, state: IRainToolCallState): IChatMessage => ({
      ...buildCustomMessage(
        `${TOOL_CALL_ID_PREFIX}${state.id}`,
        <RainToolActivity
          toolName={state.name}
          call={state}
          status={state.status}
          label={activity.label(state)}
          icon={activity.icon}
          prominent={activity.prominent}
        />,
      ),
      ...(state.startedAtMs !== undefined && {
        timestamp: epochMsToDate(state.startedAtMs),
      }),
      // So a folded run can show that something in it failed without the header
      // having to reach inside the rendered row. `ChatMessage` ignores `status`,
      // so this is inert wherever the row renders on its own.
      ...(state.status === 'error' && { status: EMessageStatus.ERROR }),
    }),
    [],
  );

  // A tool renders however its agent's profile says it should. Both exact-name
  // maps are asked before the by-shape lookup, so a tool the profile has
  // explicitly declared a row for is never captured by an open family.
  //
  // An activity row is the one tier with nothing to press: it carries no
  // Approve/Deny and no record of a decision. So a call the agent has gated
  // skips that tier for the generic card, which has both — otherwise the row
  // would shimmer forever while the agent loop stays paused on an answer the
  // user was never offered.
  const buildToolCallMessage = useCallback(
    (state: IRainToolCallState): IChatMessage => {
      const isApprovalGated =
        state.awaitingApproval === true || state.approvalDecision !== undefined;

      if (state.name !== undefined) {
        const renderer = toolRenderers[state.name];
        if (renderer) return renderer(state, renderContext);

        /*
         * An activity row is a tool call with its payload thrown away — right
         * for a curated transcript, wrong for a surface asking what the agent
         * did. Under `full` the tier is skipped so the call keeps its arguments
         * and result.
         */
        const activity = toolActivities[state.name];
        if (activity && !isApprovalGated && !isFullVerbosity) {
          return buildToolActivityMessage(activity, state);
        }

        const byShape = resolveToolRenderer?.(state.name);
        if (byShape) return byShape(state, renderContext);
      }
      return buildGenericToolCallMessage(state);
    },
    [
      buildGenericToolCallMessage,
      buildToolActivityMessage,
      isFullVerbosity,
      renderContext,
      resolveToolRenderer,
      toolActivities,
      toolRenderers,
    ],
  );

  const buildCompactionMessage = useCallback(
    (id: string, state: IRainCompactionState): IChatMessage =>
      buildCustomMessage(
        `${COMPACTION_ID_PREFIX}${id}`,
        <RainCompactionCard
          status={state.status}
          content={state.content}
          errorMessage={state.errorMessage}
        />,
      ),
    [],
  );

  const buildSleepMessage = useCallback(
    (id: string, state: IRainSleepState): IChatMessage =>
      buildCustomMessage(
        `${SLEEP_ID_PREFIX}${id}`,
        <RainSleepCard
          status={state.status}
          durationSeconds={state.durationSeconds}
          sleptForSeconds={state.sleptForSeconds}
          reason={state.reason}
          interruptedByUser={state.interruptedByUser}
          startedAt={state.startedAt}
          onWake={() => wakeThreadRef.current()}
        />,
      ),
    [wakeThreadRef],
  );

  const buildFollowUpMessage = useCallback(
    (state: IRainFollowUpState): IChatMessage =>
      buildCustomMessage(
        `${FOLLOW_UP_ID_PREFIX}${state.followUpId}`,
        <RainFollowUpCard
          followUpId={state.followUpId}
          suggestions={state.suggestions}
          isSubmitting={state.isSubmitting}
          onSend={text => sendFollowUpRef.current(text)}
        />,
      ),
    [sendFollowUpRef],
  );

  const buildMaxIterationsMessage = useCallback(
    (state: IRainMaxIterationsState): IChatMessage =>
      buildCustomMessage(
        `${MAX_ITERATIONS_ID_PREFIX}${state.id}`,
        <RainMaxIterationsCard
          turnCount={state.turnCount}
          answered={state.answered}
          choseContinue={state.choseContinue}
          isSubmitting={state.isSubmitting}
          onChoose={shouldContinue =>
            resolveMaxIterationsRef.current(state.id, shouldContinue)
          }
        />,
      ),
    [resolveMaxIterationsRef],
  );

  const buildTransferMarkerMessage = useCallback(
    (state: IRainTransferState, rootAgentId: string): IChatMessage => {
      const id = `${TRANSFER_MARKER_ID_PREFIX}${state.transferId}`;
      const isBack =
        !state.to || state.to === 'main' || state.to === rootAgentId;
      // A return still in flight draws nothing; it appears once enacted.
      if (isBack && state.status === 'pending') {
        return buildCustomMessage(id, null);
      }
      // Both legs name the agent that did the work, never the root agent.
      const agent = resolveRainAgentIdentity(
        isBack ? (state.from ?? rootAgentId) : state.to,
        t,
      );

      let status: TRainTransferStatus = state.status;
      let label: string;
      if (state.status === 'failed') {
        label = t('rain.transfer.failed', {
          reason: state.failureReason ?? '',
        });
      } else if (isBack) {
        status = 'finished';
        label =
          state.handoffStartedAtMs !== undefined &&
          state.completedAtMs !== undefined
            ? t('rain.transfer.finishedIn', {
                duration: formatDurationUltraCompact(
                  (state.completedAtMs - state.handoffStartedAtMs) / 1000,
                ),
              })
            : t('rain.transfer.finished');
      } else {
        if (state.returnTransferId) status = 'returned';
        label = t('rain.transfer.pickedUp');
      }

      return buildCustomMessage(
        id,
        <RainTransferMarker agent={agent} status={status} label={label} />,
      );
    },
    [t],
  );

  const buildSubAgentTaskMessage = useCallback(
    (state: IRainSubAgentTaskState): IChatMessage => {
      // subAgentId can be the empty string when the proto doesn't carry one
      // (handleTaskStart normalizes undefined → ''), so use `||` rather than
      // `??` to skip empty strings and reach the i18n fallback.
      const subAgentName = state.subAgentId || t('rain.subAgent.fallbackName');
      const elapsedMs = (state.completedAt ?? Date.now()) - state.startedAt;
      return buildCustomMessage(
        `${SUB_AGENT_TASK_ID_PREFIX}${state.callId}`,
        <RainSubAgentTaskCard
          subAgentName={subAgentName}
          input={state.input}
          status={state.status}
          elapsedMs={elapsedMs}
          result={state.result}
          errorMessage={state.errorMessage}
          nestedMessages={state.nestedMessages}
        />,
      );
    },
    [t],
  );

  return {
    buildInterruptMessage,
    buildToolCallMessage,
    buildCompactionMessage,
    buildSleepMessage,
    buildSubAgentTaskMessage,
    buildFollowUpMessage,
    buildMaxIterationsMessage,
    buildTransferMarkerMessage,
  };
}
